import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  MapPin,
  HomeIcon,
  Fingerprint,
  Briefcase,
  ShieldCheck,
} from "lucide-react";
import { Reveal, Overline, MaskedHeading } from "@/components/ui-bits";
import { stagger, fadeUp, viewport } from "@/lib/motion";

const FEATURES = [
  {
    icon: Search,
    title: "Diaspora Due Diligence",
    tag: "Verify before you commit.",
    text: "Verifying people, businesses, investments, and property in Ghana on behalf of clients abroad. Person verification, partner/vendor due diligence, local fact-finding.",
  },
  {
    icon: MapPin,
    title: "Property Verification",
    tag: "Ownership, boundaries, chain of title.",
    text: "Title deed authentication, land registry search, boundary confirmation, seller/developer checks, ownership chain tracing — for buyers near or far.",
  },
  {
    icon: HomeIcon,
    title: "Ghana Settlement Services",
    tag: "Come home with a plan.",
    text: "Citizenship and residency guidance, business registration coordination, and relocation project management for those building a new life in Ghana.",
  },
  {
    icon: Fingerprint,
    title: "Private Investigations",
    tag: "Discreet, professional, methodical.",
    text: "Background investigations, fraud and deception enquiries, missing-person enquiries, relationship due diligence, and fact-finding assignments.",
  },
  {
    icon: Briefcase,
    title: "Business Intelligence",
    tag: "Know who you're really dealing with.",
    text: "Company background checks, business-partner due diligence, supplier/vendor verification, and fraud-risk assessments.",
  },
  {
    icon: ShieldCheck,
    title: "Security & Risk",
    tag: "Protect what you've built.",
    text: "Security assessments, business security reviews, event security planning, and security consultancy.",
  },
];

export default function Solution() {
  return (
    <section
      id="solution"
      className="relative bg-navy grain py-24 sm:py-32 overflow-hidden"
      data-testid="solution-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Overline index={2}>What We Do</Overline>
          </Reveal>
          <MaskedHeading
            tag="h2"
            className="mt-6 font-display font-bold text-white leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl"
            lines={["Your Eyes & Ears", "on the Ground in Ghana"]}
          />
          <Reveal delay={0.1}>
            <p className="mt-7 text-white/70 leading-relaxed">
              I don't sell property. I don't broker deals. I don't take referral
              fees. Whether my investigation says{" "}
              <span className="text-gold">'proceed'</span> or{" "}
              <span className="text-gold">'walk away,'</span> my fee stays the
              same. That independence is your protection.
            </p>
          </Reveal>
        </div>

        <motion.div
          variants={stagger(0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                variants={fadeUp}
                className="group relative bg-navy-surface/60 border border-white/10 hover:border-gold/50 p-8 transition-colors duration-500"
                data-testid={`solution-card-${i + 1}`}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 text-gold group-hover:bg-gold group-hover:text-navy-deep transition-colors duration-500">
                  <Icon size={22} />
                </div>
                <h3 className="mt-6 font-display text-2xl font-semibold text-white">
                  {f.title}
                </h3>
                <p className="mt-2 text-gold text-sm font-medium">{f.tag}</p>
                <p className="mt-3 text-white/60 leading-relaxed text-[15px]">
                  {f.text}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Gold ribbon banner */}
      <Reveal className="relative z-10 mt-20">
        <div className="bg-gold text-navy-deep py-6">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="font-display text-xl sm:text-2xl font-semibold">
              You have the vision. We have the intelligence on the ground.
            </span>
            <span className="uppercase tracking-[0.25em] text-xs font-bold">
              Local Intelligence · Global Confidence
            </span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
